import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { invoiceAPI, customerAPI, projectAPI, recurringAPI } from '../api/client';
import Layout from '../components/Layout';
import { projectTypes } from '../utils/contractGenerator';
import { useToast } from '../components/ToastProvider';
import RecordPreviewModal from '../components/RecordPreviewModal';

function Invoices() {
  const [invoices, setInvoices] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [recurringInvoices, setRecurringInvoices] = useState([]);
  const [loadError, setLoadError] = useState('');
  const [showRecurringForm, setShowRecurringForm] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [previewRecurringInvoice, setPreviewRecurringInvoice] = useState(null);
  const [invoiceMode, setInvoiceMode] = useState('other');
  const [formData, setFormData] = useState({ 
    customer_id: '', 
    project_id: '',
    amount: '', 
    description: '', 
    due_date: '',
    invoice_date: new Date().toISOString().split('T')[0],
    tax_percentage: 0,
    payment_terms: '',
    notes: '',
    project_type: 'technical'
  });
  const [recurringFormData, setRecurringFormData] = useState({
    customer_id: '',
    project_id: '',
    template_name: '',
    amount: '',
    tax_percentage: 0,
    description: '',
    payment_terms: '',
    notes: '',
    project_type: 'technical',
    frequency: 'monthly',
    start_date: new Date().toISOString().split('T')[0],
    end_date: '',
  });
  const navigate = useNavigate();
  const { showToast } = useToast();

  const loadCustomers = async () => {
    try {
      const response = await customerAPI.getAll({ limit: 1000 });
      setCustomers(response.data.data || response.data || []);
    } catch (err) {
      console.error('Failed to load customers', err);
    }
  };

  const loadInvoices = useCallback(async () => {
    try {
      const response = await invoiceAPI.getAll({ limit: 1000 });
      setInvoices(response.data.data || response.data);
      setLoadError('');
    } catch (err) {
      console.error('Failed to load invoices', err);
      const message = err.response?.data?.error || 'Could not load invoices right now.';
      setLoadError(message);
      showToast({
        title: 'Invoice load failed',
        message,
        tone: 'error',
      });
    }
  }, [showToast]);

  useEffect(() => {
    loadInvoices();
    loadCustomers();
    loadProjects();
    loadRecurringInvoices();
  }, [loadInvoices]);

  const loadProjects = async () => {
    try {
      const response = await projectAPI.getAll();
      const allProjects = response.data || [];
      setProjects(allProjects.filter((project) => String(project.status).toLowerCase() !== 'completed'));
    } catch (err) {
      console.error('Failed to load projects', err);
    }
  };

  const loadRecurringInvoices = async () => {
    try {
      const response = await recurringAPI.getInvoices();
      setRecurringInvoices(response.data || []);
    } catch (err) {
      console.error('Failed to load recurring invoices', err);
    }
  };

  const resetForm = () => {
    setInvoiceMode('other');
    setFormData({
      customer_id: '', 
      project_id: '',
      amount: '', 
      description: '', 
      due_date: '',
      invoice_date: new Date().toISOString().split('T')[0],
      tax_percentage: 0,
      payment_terms: '',
      notes: '',
      project_type: 'technical'
    });
  };

  const resetRecurringForm = () => {
    setRecurringFormData({
      customer_id: '',
      project_id: '',
      template_name: '',
      amount: '',
      tax_percentage: 0,
      description: '',
      payment_terms: '',
      notes: '',
      project_type: 'technical',
      frequency: 'monthly',
      start_date: new Date().toISOString().split('T')[0],
      end_date: '',
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await invoiceAPI.create(formData);
      resetForm();
      setShowForm(false);
      loadInvoices();
      showToast({
        title: 'Invoice created',
        message: 'The invoice has been added successfully.',
        tone: 'success',
      });
    } catch (err) {
      console.error('Failed to create invoice', err);
      showToast({
        title: 'Invoice not created',
        message: err.response?.data?.error || 'Please try again.',
        tone: 'error',
      });
    }
  };

  const handleRecurringSubmit = async (e) => {
    e.preventDefault();
    try {
      await recurringAPI.createInvoice({
        ...recurringFormData,
        project_id: recurringFormData.project_id || null,
        end_date: recurringFormData.end_date || null,
      });
      resetRecurringForm();
      setShowRecurringForm(false);
      loadRecurringInvoices();
      showToast({
        title: 'Recurring schedule saved',
        message: 'Future invoices will be generated automatically.',
        tone: 'success',
      });
    } catch (err) {
      console.error('Failed to create recurring invoice', err);
      showToast({
        title: 'Schedule not saved',
        message: err.response?.data?.error || 'Please try again.',
        tone: 'error',
      });
    }
  };

  const handleStatusUpdate = async (id, newStatus) => {
    try {
      await invoiceAPI.update(id, { status: newStatus });
      loadInvoices();
    } catch (err) {
      console.error('Failed to update invoice status', err);
    }
  };

  const handleRecurringStatusUpdate = async (id, recurringInvoice, status) => {
    try {
      await recurringAPI.updateInvoice(id, {
        ...recurringInvoice,
        status,
      });
      loadRecurringInvoices();
      showToast({
        title: 'Schedule updated',
        message: `Recurring invoice is now ${status}.`,
        tone: 'success',
      });
    } catch (err) {
      console.error('Failed to update recurring invoice status', err);
      showToast({
        title: 'Schedule not updated',
        message: err.response?.data?.error || 'Please try again.',
        tone: 'error',
      });
    }
  };

  const handleDeleteRecurringInvoice = async (id) => {
    if (!window.confirm('Delete this recurring invoice schedule?')) {
      return;
    }

    try {
      await recurringAPI.deleteInvoice(id);
      loadRecurringInvoices();
      showToast({
        title: 'Schedule deleted',
        message: 'The recurring invoice template has been removed.',
        tone: 'success',
      });
    } catch (err) {
      console.error('Failed to delete recurring invoice', err);
      showToast({
        title: 'Delete failed',
        message: err.response?.data?.error || 'Please try again.',
        tone: 'error',
      });
    }
  };

  const handleSendReminder = async (invoiceId) => {
    try {
      await invoiceAPI.sendReminder(invoiceId, { stage: 'manual_follow_up' });
      showToast({
        title: 'Reminder logged',
        message: 'The payment reminder was recorded for this invoice.',
        tone: 'success',
      });
    } catch (err) {
      console.error('Failed to log reminder', err);
      showToast({
        title: 'Reminder failed',
        message: err.response?.data?.error || 'Please try again.',
        tone: 'error',
      });
    }
  };

  const getCustomerName = (customerId) => {
    const customer = customers.find((c) => String(c.id) === String(customerId));
    return customer ? customer.name : `Customer ${customerId}`;
  };

  const handleProjectSelect = (projectId) => {
    const selectedProject = projects.find((project) => String(project.id) === String(projectId));
    const matchedCustomer = selectedProject
      ? customers.find((customer) => customer.name?.trim().toLowerCase() === selectedProject.clientName?.trim().toLowerCase())
      : null;

    setFormData((prev) => ({
      ...prev,
      project_id: projectId,
      customer_id: matchedCustomer ? String(matchedCustomer.id) : prev.customer_id,
      amount: selectedProject?.budget ? String(selectedProject.budget) : prev.amount,
      description: selectedProject
        ? prev.description || selectedProject.projectName || selectedProject.description || ''
        : prev.description,
    }));
  };

  const handleRecurringProjectSelect = (projectId) => {
    const selectedProject = projects.find((project) => String(project.id) === String(projectId));
    const matchedCustomer = selectedProject
      ? customers.find((customer) => customer.name?.trim().toLowerCase() === selectedProject.clientName?.trim().toLowerCase())
      : null;

    setRecurringFormData((prev) => ({
      ...prev,
      project_id: projectId,
      customer_id: matchedCustomer ? String(matchedCustomer.id) : prev.customer_id,
      amount: selectedProject?.budget ? String(selectedProject.budget) : prev.amount,
      description: selectedProject
        ? prev.description || selectedProject.projectName || selectedProject.description || ''
        : prev.description,
      template_name: selectedProject?.projectName || prev.template_name,
    }));
  };

  return (
    <Layout pageTitle="Invoices" pageSubtitle="Manage your invoices and billing">
      <div className="flex justify-end mb-8">
        <button 
          onClick={() => {
            if (showForm) {
              resetForm();
            }
            setShowForm(!showForm);
          }}
          className="px-6 py-2.5 bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-semibold rounded-xl hover:from-blue-700 hover:to-indigo-700 transition-all duration-200 shadow-lg shadow-blue-500/20"
        >
          {showForm ? 'Cancel' : '+ New Invoice'}
        </button>
      </div>

      {showForm && (
        <div className="bg-white rounded-2xl p-8 shadow-lg mb-8">
          <h3 className="text-lg font-bold text-gray-900 mb-6">Create New Invoice</h3>
          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-3">Invoice For</label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <button
                    type="button"
                    onClick={() => {
                      setInvoiceMode('project');
                      setFormData((prev) => ({ ...prev, project_id: prev.project_id || '' }));
                    }}
                    className={`rounded-xl border px-4 py-4 text-left transition ${
                      invoiceMode === 'project'
                        ? 'border-blue-500 bg-blue-50 text-blue-900'
                        : 'border-gray-200 bg-white text-gray-700 hover:border-blue-300'
                    }`}
                  >
                    <p className="font-semibold">Create Invoice for a Project</p>
                    <p className="mt-1 text-sm text-gray-500">Link this invoice to an active project.</p>
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setInvoiceMode('other');
                      setFormData((prev) => ({ ...prev, project_id: '' }));
                    }}
                    className={`rounded-xl border px-4 py-4 text-left transition ${
                      invoiceMode === 'other'
                        ? 'border-blue-500 bg-blue-50 text-blue-900'
                        : 'border-gray-200 bg-white text-gray-700 hover:border-blue-300'
                    }`}
                  >
                    <p className="font-semibold">Create Invoice for Others</p>
                    <p className="mt-1 text-sm text-gray-500">Create a regular invoice not linked to a project.</p>
                  </button>
                </div>
              </div>

              {invoiceMode === 'project' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Project *</label>
                  <select
                    value={formData.project_id}
                    onChange={(e) => handleProjectSelect(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition bg-white"
                    required
                  >
                    <option value="">-- Select Active Project --</option>
                    {projects.map((project) => (
                      <option key={project.id} value={project.id}>
                        {project.projectName} ({project.clientName}) - {project.status}
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500 mt-1">Completed projects are excluded from this list.</p>
                </div>
              )}

              {/* Customer */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Customer *</label>
                <select
                  value={formData.customer_id}
                  onChange={(e) => setFormData({ ...formData, customer_id: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition bg-white"
                  required
                >
                  <option value="">-- Select Customer --</option>
                  {customers.map((customer) => (
                    <option key={customer.id} value={customer.id}>
                      {customer.name} ({customer.email})
                    </option>
                  ))}
                </select>
              </div>

              {/* Invoice Date */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Invoice Date</label>
                <input
                  type="date"
                  value={formData.invoice_date}
                  onChange={(e) => setFormData({ ...formData, invoice_date: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition"
                />
              </div>

              {/* Project Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Project Type *</label>
                <select
                  value={formData.project_type}
                  onChange={(e) => setFormData({ ...formData, project_type: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition bg-white"
                  required
                >
                  {projectTypes.map((type) => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">Determines the contract template for this invoice</p>
              </div>

              {/* Total Amount */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Total Amount (₹) *</label>
                <input
                  type="number"
                  step="0.01"
                  placeholder="0.00"
                  value={formData.amount}
                  onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition"
                  required
                />
              </div>

              {/* Tax Percentage */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Tax Percentage (%)</label>
                <input
                  type="number"
                  step="0.01"
                  placeholder="0.00"
                  value={formData.tax_percentage}
                  onChange={(e) => setFormData({ ...formData, tax_percentage: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition"
                />
              </div>

              {/* Description */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                <input
                  type="text"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition"
                  placeholder="e.g., Professional Services, Products"
                />
              </div>

              {/* Payment Terms */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Payment Terms</label>
                <input
                  type="text"
                  value={formData.payment_terms}
                  onChange={(e) => setFormData({ ...formData, payment_terms: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition"
                  placeholder="e.g., Net 30, Due on Receipt"
                />
              </div>

              {/* Due Date */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Due Date *</label>
                <input
                  type="date"
                  value={formData.due_date}
                  onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition"
                  required
                />
              </div>

              {/* Notes */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">Notes</label>
                <textarea
                  rows="3"
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition resize-none"
                  placeholder="Additional notes or terms"
                />
              </div>

              {/* Currency Info */}
              <div className="md:col-span-2 bg-blue-50/80 p-4 rounded-xl border border-blue-200">
                <p className="text-sm text-blue-800">
                  <strong>Currency:</strong> Indian Rupee (₹) - All amounts in INR
                </p>
              </div>

              {/* Submit Button */}
              <button 
                type="submit" 
                className="md:col-span-2 px-6 py-2.5 bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-semibold rounded-xl hover:from-blue-700 hover:to-indigo-700 transition-all duration-200 shadow-lg shadow-blue-500/20"
              >
                Create Invoice
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="bg-white rounded-2xl p-8 shadow-lg mb-8">
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div>
            <h3 className="text-lg font-bold text-gray-900">Recurring Billing Templates</h3>
            <p className="text-sm text-gray-500">Create schedules that automatically generate invoices in the background.</p>
          </div>
          <button
            type="button"
            onClick={() => {
              if (showRecurringForm) {
                resetRecurringForm();
              }
              setShowRecurringForm((current) => !current);
            }}
            className="rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:from-blue-700 hover:to-indigo-700 shadow-lg shadow-blue-500/20"
          >
            {showRecurringForm ? 'Close Schedule Form' : '+ New Recurring Schedule'}
          </button>
        </div>

        {showRecurringForm && (
          <form onSubmit={handleRecurringSubmit} className="mt-6 border-t border-gray-100 pt-6">
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700">Template Name *</label>
                <input
                  type="text"
                  value={recurringFormData.template_name}
                  onChange={(e) => setRecurringFormData({ ...recurringFormData, template_name: e.target.value })}
                  className="w-full rounded-lg border border-gray-300 px-4 py-2 outline-none transition focus:border-transparent focus:ring-2 focus:ring-blue-500"
                  placeholder="Monthly retainership"
                  required
                />
              </div>
              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700">Frequency *</label>
                <select
                  value={recurringFormData.frequency}
                  onChange={(e) => setRecurringFormData({ ...recurringFormData, frequency: e.target.value })}
                  className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 outline-none transition focus:border-transparent focus:ring-2 focus:ring-blue-500"
                  required
                >
                  <option value="weekly">Weekly</option>
                  <option value="bi-weekly">Bi-Weekly</option>
                  <option value="monthly">Monthly</option>
                  <option value="quarterly">Quarterly</option>
                  <option value="semi-annual">Semi-Annual</option>
                  <option value="annual">Annual</option>
                </select>
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700">Project</label>
                <select
                  value={recurringFormData.project_id}
                  onChange={(e) => handleRecurringProjectSelect(e.target.value)}
                  className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 outline-none transition focus:border-transparent focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">-- No Project Link --</option>
                  {projects.map((project) => (
                    <option key={project.id} value={project.id}>
                      {project.projectName} ({project.clientName})
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700">Customer *</label>
                <select
                  value={recurringFormData.customer_id}
                  onChange={(e) => setRecurringFormData({ ...recurringFormData, customer_id: e.target.value })}
                  className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 outline-none transition focus:border-transparent focus:ring-2 focus:ring-blue-500"
                  required
                >
                  <option value="">-- Select Customer --</option>
                  {customers.map((customer) => (
                    <option key={customer.id} value={customer.id}>
                      {customer.name} ({customer.email})
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700">Amount *</label>
                <input
                  type="number"
                  step="0.01"
                  value={recurringFormData.amount}
                  onChange={(e) => setRecurringFormData({ ...recurringFormData, amount: e.target.value })}
                  className="w-full rounded-lg border border-gray-300 px-4 py-2 outline-none transition focus:border-transparent focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700">Tax Percentage</label>
                <input
                  type="number"
                  step="0.01"
                  value={recurringFormData.tax_percentage}
                  onChange={(e) => setRecurringFormData({ ...recurringFormData, tax_percentage: e.target.value })}
                  className="w-full rounded-lg border border-gray-300 px-4 py-2 outline-none transition focus:border-transparent focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700">Start Date *</label>
                <input
                  type="date"
                  value={recurringFormData.start_date}
                  onChange={(e) => setRecurringFormData({ ...recurringFormData, start_date: e.target.value })}
                  className="w-full rounded-lg border border-gray-300 px-4 py-2 outline-none transition focus:border-transparent focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700">End Date</label>
                <input
                  type="date"
                  value={recurringFormData.end_date}
                  onChange={(e) => setRecurringFormData({ ...recurringFormData, end_date: e.target.value })}
                  className="w-full rounded-lg border border-gray-300 px-4 py-2 outline-none transition focus:border-transparent focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700">Project Type *</label>
                <select
                  value={recurringFormData.project_type}
                  onChange={(e) => setRecurringFormData({ ...recurringFormData, project_type: e.target.value })}
                  className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 outline-none transition focus:border-transparent focus:ring-2 focus:ring-blue-500"
                  required
                >
                  {projectTypes.map((type) => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700">Payment Terms</label>
                <input
                  type="text"
                  value={recurringFormData.payment_terms}
                  onChange={(e) => setRecurringFormData({ ...recurringFormData, payment_terms: e.target.value })}
                  className="w-full rounded-lg border border-gray-300 px-4 py-2 outline-none transition focus:border-transparent focus:ring-2 focus:ring-blue-500"
                  placeholder="Net 30"
                />
              </div>

              <div className="md:col-span-2">
                <label className="mb-2 block text-sm font-medium text-gray-700">Description</label>
                <input
                  type="text"
                  value={recurringFormData.description}
                  onChange={(e) => setRecurringFormData({ ...recurringFormData, description: e.target.value })}
                  className="w-full rounded-lg border border-gray-300 px-4 py-2 outline-none transition focus:border-transparent focus:ring-2 focus:ring-blue-500"
                  placeholder="Monthly support services"
                />
              </div>

              <div className="md:col-span-2">
                <label className="mb-2 block text-sm font-medium text-gray-700">Notes</label>
                <textarea
                  rows="3"
                  value={recurringFormData.notes}
                  onChange={(e) => setRecurringFormData({ ...recurringFormData, notes: e.target.value })}
                  className="w-full resize-none rounded-lg border border-gray-300 px-4 py-2 outline-none transition focus:border-transparent focus:ring-2 focus:ring-blue-500"
                  placeholder="Internal notes for this recurring billing schedule"
                />
              </div>

              <button
                type="submit"
                className="md:col-span-2 rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-2.5 font-semibold text-white transition hover:from-blue-700 hover:to-indigo-700 shadow-lg shadow-blue-500/20"
              >
                Save Recurring Schedule
              </button>
            </div>
          </form>
        )}

        <div className="mt-6 overflow-x-auto">
          <table className="w-full">
            <thead className="border-b border-gray-200 bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Template</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Customer</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Frequency</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Next Run</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Amount</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Status</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {recurringInvoices.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-4 py-6 text-center text-sm text-gray-500">
                    No recurring invoice schedules yet.
                  </td>
                </tr>
              ) : recurringInvoices.map((recurringInvoice) => (
                <tr
                  key={recurringInvoice.id}
                  className="cursor-pointer hover:bg-slate-50"
                  onClick={() => setPreviewRecurringInvoice(recurringInvoice)}
                >
                  <td className="px-4 py-3 text-sm font-medium text-gray-900">{recurringInvoice.template_name || `Schedule #${recurringInvoice.id}`}</td>
                  <td className="px-4 py-3 text-sm text-gray-700">{recurringInvoice.customer_name || getCustomerName(recurringInvoice.customer_id)}</td>
                  <td className="px-4 py-3 text-sm text-gray-700">{recurringInvoice.frequency}</td>
                  <td className="px-4 py-3 text-sm text-gray-700">{new Date(recurringInvoice.next_invoice_date).toLocaleDateString()}</td>
                  <td className="px-4 py-3 text-sm font-semibold text-gray-900">₹{parseFloat(recurringInvoice.amount || 0).toFixed(2)}</td>
                  <td className="px-4 py-3">
                    <select
                      value={recurringInvoice.status || 'active'}
                      onClick={(e) => e.stopPropagation()}
                      onChange={(e) => handleRecurringStatusUpdate(recurringInvoice.id, recurringInvoice, e.target.value)}
                      className="rounded-lg border-0 bg-slate-100 px-3 py-1 text-xs font-medium text-slate-800 outline-none"
                    >
                      <option value="active">Active</option>
                      <option value="paused">Paused</option>
                      <option value="completed">Completed</option>
                    </select>
                  </td>
                  <td className="px-4 py-3 text-sm">
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteRecurringInvoice(recurringInvoice.id);
                      }}
                      className="font-medium text-rose-600 transition hover:text-rose-800"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {previewRecurringInvoice && (
        <RecordPreviewModal
          title={previewRecurringInvoice.template_name || `Schedule #${previewRecurringInvoice.id}`}
          subtitle="Recurring Invoice"
          onClose={() => setPreviewRecurringInvoice(null)}
          fields={[
            { label: 'Customer', value: previewRecurringInvoice.customer_name || getCustomerName(previewRecurringInvoice.customer_id) },
            { label: 'Frequency', value: previewRecurringInvoice.frequency },
            { label: 'Next Invoice Date', value: previewRecurringInvoice.next_invoice_date ? new Date(previewRecurringInvoice.next_invoice_date).toLocaleDateString() : 'N/A' },
            { label: 'Amount', value: `₹${parseFloat(previewRecurringInvoice.amount || 0).toFixed(2)}` },
            { label: 'Status', value: previewRecurringInvoice.status || 'active' },
            { label: 'Description', value: previewRecurringInvoice.description },
            { label: 'Payment Terms', value: previewRecurringInvoice.payment_terms },
          ]}
        />
      )}

      <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
        {loadError ? (
          <div className="border-b border-rose-200 bg-rose-50 px-6 py-4 text-sm text-rose-700">
            {loadError}
          </div>
        ) : null}
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">ID</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Customer</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Amount</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Status</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Due Date</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Reminder</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {invoices.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-6 py-10 text-center text-sm text-gray-500">
                    No invoices found for this account yet.
                  </td>
                </tr>
              ) : invoices.map((invoice) => (
                <tr
                  key={invoice.id}
                  className="hover:bg-gray-50 transition cursor-pointer"
                  onClick={() => navigate(`/invoices/${invoice.id}/preview`)}
                >
                  <td className="px-6 py-4 text-sm text-gray-900">#{invoice.id}</td>
                  <td className="px-6 py-4 text-sm text-gray-700">{getCustomerName(invoice.customer_id)}</td>
                  <td className="px-6 py-4 text-sm font-semibold text-gray-900">${invoice.amount}</td>
                  <td className="px-6 py-4">
                    <select
                      value={invoice.status || 'draft'}
                      onClick={(e) => e.stopPropagation()}
                      onChange={(e) => handleStatusUpdate(invoice.id, e.target.value)}
                      className={`px-3 py-1 rounded-lg text-xs font-medium border-0 outline-none cursor-pointer transition ${
                        invoice.status === 'paid' ? 'bg-green-100 text-green-800' :
                        invoice.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                        invoice.status === 'partially_paid' ? 'bg-orange-100 text-orange-800' :
                        'bg-blue-100 text-blue-800'
                      }`}
                    >
                      <option value="draft">Draft</option>
                      <option value="pending">Pending</option>
                      <option value="partially_paid">Partially Paid</option>
                      <option value="paid">Paid</option>
                    </select>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-700">{new Date(invoice.due_date).toLocaleDateString()}</td>
                  <td className="px-6 py-4 text-sm">
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleSendReminder(invoice.id);
                      }}
                      className="font-medium text-blue-600 transition hover:text-blue-800"
                    >
                      Log Reminder
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </Layout>
  );
}

export default Invoices;
